import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Col, Divider, notification, Row } from 'antd'
import type { RootState } from '@src/store'
import type { IActionsCallbacks, ILang, ITemplate } from '@src/interfaces'
import { createTemplate, getPreview, updateTemplate } from '@src/services/config.service'
import {
  activateVariables,
  clearTemplateStore,
  goTo,
  redirectInit,
  updateTemplate as updateTemplateStore
} from '@src/slices/configuration.slice'
import { notificationAlert } from '@src/modules/atoms/notification'
import { Paths } from '@src/enums/paths'
import { startDownloadFileByBlob } from '@src/helpers/start-download-file'
import styles from './styles.module.scss'

interface IEditableButtonsFooter {
  template: ITemplate
  isNew: boolean
  disabled?: boolean
  updateTemplateCopy: () => void
  lang: ILang
  actionsCallback?: IActionsCallbacks
  handleReturn?: () => void
  handleSearch?: () => void
}

const EditableButtonsFooter: React.FC<IEditableButtonsFooter> = ({
  template,
  isNew,
  updateTemplateCopy,
  lang,
  actionsCallback,
  handleReturn,
  handleSearch,
  disabled
}) => {
  const dispatch = useDispatch()
  const baseProps = useSelector(({ baseProps }: RootState) => baseProps)

  const onCancel = () => {
    handleReturn?.()
    handleSearch?.()
    dispatch(activateVariables({ visibility: false }))
    dispatch(clearTemplateStore({ isNew }))
    dispatch(redirectInit())
  }

  const onPreview = () => {
    const { workspace, content } = template
    notificationAlert({
      key: 'onPreviewNotification',
      message: 'Generando archivo...',
      description: 'Tu archivo está siendo generado, se mostrará en un segundos más.',
      variant: 'info'
    })

    const { code: langCode } = lang
    getPreview(baseProps, workspace?.token!, content, langCode).then((blob) => {
      notification.close('onPreviewNotification')
      startDownloadFileByBlob(blob)
    })
  }

  const onSave = () => {
    const { title, workspace, content, sign_order, tags, document_type, biometric_authentication } = template
    const currentTemplate = {
      title,
      workspace: workspace?.token!,
      content,
      document_type,
      sign_order: sign_order ? JSON.stringify(sign_order) : null,
      tags,
      biometric_authentication
    }
    const { code: langCode } = lang

    if (isNew) {
      fetchCreateTemplate(currentTemplate, langCode)
      return
    }
    handleSearch?.()
    fetchUpdateTemplate(currentTemplate, langCode)
  }

  const fetchCreateTemplate = (newTemplate: any, lang: string) => {
    const actionFunc = actionsCallback?.createTemplate || createTemplate
    actionFunc?.(baseProps, newTemplate, lang).then(({ success, data: template }) => {
      if (success) {
        notificationAlert({
          message: 'Nueva plantilla creada',
          description: 'La plantilla se ha creado exitosamente',
          variant: 'success'
        })
        dispatch(activateVariables({ visibility: false }))
        dispatch(clearTemplateStore({ isNew }))
        dispatch(
          updateTemplateStore({
            prop: 'token',
            value: template.token,
            isNew: false
          })
        )
        handleReturn?.()
        dispatch(goTo(Paths.main))
      }
    })
  }

  const fetchUpdateTemplate = (updatedTemplate: any, lang: string) => {
    const actionFunc = actionsCallback?.updateTemplate || updateTemplate
    actionFunc(baseProps, template.token!, updatedTemplate, lang).then(({ success }) => {
      if (success) {
        updateTemplateCopy()
        handleReturn?.()
        notificationAlert({
          message: 'Plantilla actualizada',
          description: 'La plantilla se ha modificado correctamente',
          variant: 'success'
        })
      }
    })
  }

  return (
    <div className={styles.rankmiTemplateButtonsContainer}>
      <Row>
        <Col span={24}>
          <Divider />
        </Col>
        <Col span={12}>
          <Button className='rkm-btn-success btn-preview' size='large' onClick={onPreview}>
            Vista previa
          </Button>
        </Col>
        <Col span={12}>
          <Button type='primary' size='large' onClick={onSave} disabled={disabled}>
            Guardar plantilla
          </Button>
          <Button className='rkm-btn-secondary' size='large' onClick={onCancel}>
            Cancelar
          </Button>
        </Col>
      </Row>
    </div>
  )
}

export default EditableButtonsFooter
